import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngxs/store';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { Feature } from 'app/library/feature/models/feature.model';
import { ModuleTypes } from 'app/library/module/models/module-types';
import { SearchMyModules } from 'app/library/my-profile-modules/actions';
import { MyModulesStateModel } from 'app/library/my-profile-modules/models/my-modules.state-model';
import { TagMultiSelectService } from 'app/library/tag/services/multi-select.service';
import { TagService } from 'app/library/tag/services/tag.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-my-modules-tollbar',
    templateUrl: './my-modules-toolbar.component.html',
    styleUrls: ['./my-modules-toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class MyModulesToolbarComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    feature$: Observable<Feature[]>;
    features: Feature[];

    @Input()
    profile$: Observable<User>;
    profile: User;

    @Input()
    isVisibleNewModuleButton: boolean;

    @Input()
    isVisibleToggleButtons: boolean;

    @Input()
    tagStorageKey = 'myModulesSelectedTags';

    @Input()
    searchTermStorageKey = `${MyModulesStateModel.identifier}SearchTerm`;

    @Input()
    syncMyModulesSearchState = true;

    @Output()
    viewPreferenceChanged = new EventEmitter<string>();

    @Output()
    searchTermChanged = new EventEmitter<string>();

    searchInput = new FormControl('');
    moduleTypes = ModuleTypes.all;
    modulesViewPreference: string;

    private canAddNewModule(): 'yes' | 'no' | 'private' | 'basic' {
        if (
            this.features &&
            this.features.filter((feature) => feature.id === 'module_visibility' && feature.status === 'inactive').length === 1 &&
            this.features.filter((feature) => feature.id === 'e_learning' && feature.status === 'inactive').length === 1 &&
            this.profile &&
            this.profile.permissions.indexOf(AdminPermissions.TENANT_MANAGEMENT) !== -1
        ) {
            return 'basic';
        }

        if (this.profile && this.profile.permissions.indexOf(AdminPermissions.TENANT_MANAGEMENT) !== -1) {
            return 'yes';
        }

        if (
            this.features &&
            this.features.filter((feature) => feature.id === 'module_visibility' && feature.status === 'active').length === 1 &&
            this._tenantDataService.data.canCreatePrivateModule
        ) {
            return 'private';
        }

        return 'no';
    }

    get getAddNewModuleRoute(): any[] {
        switch (this.canAddNewModule()) {
            case 'yes':
                return ['', { outlets: { modal: 'new-module' } }];
            case 'private':
                return ['', { outlets: { modal: 'new-module/private' } }];
            case 'basic':
                return ['', { outlets: { modal: 'new-module/basic' } }];
            default:
                return null;
        }
    }

    get getAddNewModuleLabel(): string {
        switch (this.canAddNewModule()) {
            case 'yes':
            case 'basic':
                return 'PAGE.ALL_MODULES.BUTTON.ADD';
            case 'private':
                return 'PAGE.ALL_MODULES.BUTTON.ADD_PRIVATE';
            default:
                return null;
        }
    }

    constructor(private _store: Store, private _tagMultiSelectService: TagMultiSelectService, private _tenantDataService: TenantDataService, public tagService: TagService) {
        super();

        this.modulesViewPreference = localStorage.getItem('modulesViewPreference');
    }

    ngOnInit(): void {
        this.searchInput.setValue(sessionStorage.getItem(this.searchTermStorageKey) || '');
        this._tagMultiSelectService.setFromStorage(this.tagStorageKey);

        this.feature$
            .pipe(
                filter((features) => !!features),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((features) => {
                this.features = features;
            });

        this.profile$
            .pipe(
                filter((profile) => !!profile),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((profile) => {
                this.profile = profile;
            });

        this.searchInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe((searchTerm: string) => {
            sessionStorage.setItem(this.searchTermStorageKey, searchTerm || '');

            if (this.syncMyModulesSearchState) {
                this._store.dispatch(new SearchMyModules(searchTerm));
            }

            this.searchTermChanged.emit(searchTerm || '');
        });
    }

    storeViewPreference(value: string) {
        localStorage.setItem('modulesViewPreference', value);
        this.viewPreferenceChanged.emit(value);
    }
}
